import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, where, getDocs } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import TopBar from '../components/Layout/TopBar';
import { fetchEquipesChampionnat } from '../api/fff';
import APIDebugPanel from '../components/Layout/APIDebugPanel';

const Equipes = () => {
  const [equipes, setEquipes] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEquipes = async () => {
      setLoading(true);
      // 1. Récupère la liste ordonnée des championnats via l'API FFF
      const apiEquipes = await fetchEquipesChampionnat();
      // 2. Récupère toutes les équipes Firestore
      const snap = await getDocs(collection(db, 'equipes'));
      const firestoreEquipes = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // 3. Fusionne les deux sources
      const merged = apiEquipes.map(eqApi => {
        // On cherche une équipe Firestore dont le nom correspond au championnat
        const eqFS = firestoreEquipes.find(
          e => e.nom?.toLowerCase() === eqApi.championnat.toLowerCase()
        );
        return {
          ...eqApi,
          photo: eqFS?.photo || '',
          slogan: eqFS?.slogan || '',
          description: eqFS?.description || '',
          slug: eqFS?.slug || eqApi.championnat.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        };
      });
      setEquipes(merged);
      setLoading(false);
    };
    fetchEquipes();
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % equipes.length);
  const prev = () => setCurrent((prev) => (prev - 1 + equipes.length) % equipes.length);

  // Swipe mobile
  let touchStartX = null;
  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 50) prev();
    if (dx < -50) next();
    touchStartX = null;
  };

  if (loading) {
    return <div className="flex justify-center items-center min-h-[300px] text-white">Chargement…</div>;
  }

  if (equipes.length === 0) {
    return <div className="text-white text-center py-12">Aucune équipe à afficher.</div>;
  }

  return (
    <>
      <TopBar />
      <div className="relative w-full min-h-[70vh] flex flex-col items-center justify-center bg-[#001B56] pb-8">
        <div className="w-full max-w-6xl mx-auto flex items-center justify-center relative z-10">
          {/* Flèche gauche */}
          <button onClick={prev} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 bg-[#FFE400] text-[#001B56] rounded-full p-3 shadow-lg hover:scale-110 transition z-20" aria-label="Précédent">
            &#8592;
          </button>
          {/* Slide équipe */}
          <div className="w-full pt-14" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={equipes[current].id}
                initial={{ opacity: 0, x: 80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -80 }}
                transition={{ duration: 0.5 }}
                className="relative flex items-center justify-center min-h-[60vh] md:min-h-[70vh]"
              >
                {/* Image de fond */}
                <div className="absolute inset-0 rounded-[3rem] overflow-hidden shadow-2xl w-full h-full flex items-center justify-center">
                  <img
                    src={equipes[current].photo}
                    alt={equipes[current].nom}
                    className="w-full h-full object-cover object-center"
                    style={{ minHeight: '60vh', maxHeight: '80vh' }}
                  />
                  {/* Overlay dégradé bleu foncé bas -> haut */}
                  <div className="absolute inset-0" style={{
                    background: 'linear-gradient(0deg, #001B56 10%, rgba(0,27,86,0.0) 100%)',
                    opacity: 0.92
                  }} />
                </div>
                {/* Contenu texte centré */}
                <div className="absolute bottom-0 left-0 w-full h-full flex flex-col items-center justify-end pb-16 px-4 z-10">
                  <h2 className="text-4xl md:text-6xl font-extrabold text-white drop-shadow-lg uppercase text-center mb-2">
                    {equipes[current]?.championnat || ''}
                  </h2>
                  <span className="text-xl md:text-2xl font-bold text-[#FFE400] tracking-wide mb-4 text-center">
                    {equipes[current].slogan}
                  </span>
                  <p className="text-white/90 max-w-2xl text-center mb-6 text-lg md:text-xl">
                    {equipes[current].description}
                  </p>
                  <Link
                    to={`/equipes/${equipes[current].slug}`}
                    className="px-8 py-4 bg-[#FFE400] text-[#001B56] font-bold rounded-full shadow-lg hover:bg-[#BDFF00] transition text-xl"
                  >
                    Je découvre →
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
          {/* Flèche droite */}
          <button onClick={next} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 bg-[#FFE400] text-[#001B56] rounded-full p-3 shadow-lg hover:scale-110 transition z-20" aria-label="Suivant">
            &#8594;
          </button>
        </div>
        {/* Pagination points */}
        <div className="flex gap-2 mt-8 z-10">
          {equipes.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrent(idx)}
              className={`w-3 h-3 rounded-full ${idx === current ? 'bg-[#FFE400]' : 'bg-white/30'} transition`}
              aria-label={`Aller à l'équipe ${idx + 1}`}
            />
          ))}
        </div>
      </div>
      
      {/* Panel de diagnostic API (développement uniquement) */}
      <APIDebugPanel />
    </>
  );
};

export default Equipes; 